/**
 * Scroll choreography of the fiber field ("Le Fil").
 *
 * One ScrollTrigger per chapter window records its own progress; the fiber
 * state is then derived from all of them at once. Overlapping windows can never
 * fight, jumps (hash links, reloads mid-page) land on the right state, and
 * scrolling back up reverses everything. Smoothing happens in the render loop.
 */
import { useEffect } from 'react';
import { gsap, ScrollTrigger } from './gsap';
import { SECTION_IDS } from '../content/site';
import { fiberState } from '../three/fiberState';

type Key = 'hero' | 'weave' | 'dim' | 'undim' | 'proof' | 'vision' | 'contact';

const WINDOWS: { key: Key; id: string; start: string; end: string }[] = [
	// The bundle drifts and thins out as the hero leaves.
	{ key: 'hero', id: SECTION_IDS.hero, start: 'top top', end: 'bottom top' },
	// Bundle -> weave behind the demonstration, complete when its scene pins (top reaches top).
	{ key: 'weave', id: SECTION_IDS.manifesto, start: 'top 85%', end: 'top top' },
	// The weave steps back behind the demonstration stage while it is pinned...
	{ key: 'dim', id: SECTION_IDS.manifesto, start: 'top 40%', end: 'top top' },
	// ...and the dim is lifted while Work arrives, so it is gone long before Vision.
	{ key: 'undim', id: SECTION_IDS.work, start: 'top bottom', end: 'top 60%' },
	// The field fades out so the proof sections breathe.
	{ key: 'proof', id: SECTION_IDS.work, start: 'top 95%', end: 'top 35%' },
	// Weave -> meadow at dusk. Same window as the Vision page background tween.
	{ key: 'vision', id: SECTION_IDS.vision, start: 'top 85%', end: 'top 20%' },
	// The meadow recedes behind the contact form.
	{ key: 'contact', id: SECTION_IDS.contact, start: 'top 80%', end: 'top 30%' },
];

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** How far the field steps back behind the pinned demonstration (opacity 1 -> 0.35). */
const DIM = 0.65;

export function useScrollChoreography(): void {
	useEffect(() => {
		const progress: Record<Key, number> = { hero: 0, weave: 0, dim: 0, undim: 0, proof: 0, vision: 0, contact: 0 };

		const apply = () => {
			fiberState.heroProgress = progress.hero;
			fiberState.morph = clamp(progress.weave + progress.vision, 0, 2);
			// The dim term is zero once Work has arrived (undim = 1): Vision and Contact are unchanged.
			const dim = DIM * progress.dim * (1 - progress.undim);
			fiberState.opacity = clamp(1 - progress.proof + progress.vision - 0.65 * progress.contact - dim, 0, 1);
			fiberState.theme = progress.vision;
		};

		// Created in a passive effect: the sections' own pins (layout effects) already exist.
		const ctx = gsap.context(() => {
			WINDOWS.forEach(({ key, id, start, end }) => {
				const trigger = document.getElementById(id);
				if (!trigger) return;
				const record = (self: ScrollTrigger) => {
					progress[key] = self.progress;
					apply();
				};
				// refreshPriority -1: measured after every pin of the page, whatever the creation order.
				ScrollTrigger.create({ trigger, start, end, refreshPriority: -1, onUpdate: record, onRefresh: record });
			});
		});
		apply();

		let alive = true;
		if (document.fonts) {
			document.fonts.ready.then(() => {
				if (alive) ScrollTrigger.refresh();
			});
		}

		return () => {
			alive = false;
			ctx.revert();
		};
	}, []);
}
